import { Period } from '@spike/model';
import clsx from 'clsx';
import Button, { ButtonProps } from 'components/UI/V3/Button/Button';
import { DateRangePicker, DateRangePickerOption } from 'components/UI/V3/DateRangePicker/DateRangePicker';
import styles from 'components/UI/V3/DateRangePickerDropdown/DateRangePickerDropdown.module.scss';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownContentProps } from 'components/UI/V3/Dropdown/Content/DropdownContent';
import { Moment } from 'moment-timezone';
import React from 'react';

export interface DateRangePickerDropdownProps {
    minDate?: Moment;
    maxDate?: Moment;
    timeZone: string;
    options?: Array<DateRangePickerOption>;

    value: DateRangePickerOption['value'];
    onChange: (value: DateRangePickerOption['value']) => void;

    buttonProps?: Partial<ButtonProps>;

    trigger?: React.ReactNode;

    dropdownProps?: Partial<Omit<DropdownContentProps, 'children'>>;
}

export const DateRangePickerDropdown: React.FC<DateRangePickerDropdownProps> = ({
    minDate,
    maxDate,
    timeZone,
    value,
    onChange,
    options = [],
    buttonProps,
    trigger,
    dropdownProps = {}
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedRange, setSelectedRange] = React.useState<Period | null>(value);
    const { className: dropdownClassName, ...restDropdownProps } = dropdownProps;

    const getButtonLabel = (): string => {
        const activeOption = options.find(option => {
            if (value === null && option.value === null) {
                return true;
            }

            if (value && option.value) {
                return value.from.isSame(option.value.from, 'day') && value.to.isSame(option.value.to, 'day');
            }

            return false;
        });

        if (activeOption) {
            return activeOption.label;
        }

        if (value?.from.isSame(value?.to, 'day')) {
            return value?.from.format('MMM D, YYYY');
        }

        if (value) {
            return `${value.from?.format('MMM D, YYYY')} - ${value.to?.format('MMM D, YYYY')}`;
        }

        return 'All time';
    };

    const triggerElement = React.useMemo(() => {
        if (trigger) {
            return trigger;
        }

        return (
            <Button
                label={getButtonLabel()}
                {...buttonProps}
            />
        );
    }, [value, buttonProps, trigger]);

    const changeHandler = React.useCallback(
        (range: Period | null) => {
            setIsOpen(false);
            setSelectedRange(range);
            onChange(range);
        },
        [onChange]
    );

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <DropdownTrigger>{triggerElement}</DropdownTrigger>

            <DropdownContent
                {...restDropdownProps}
                showBottomModalOnMobile={false}
                className={clsx(styles.dropdown, dropdownClassName)}
            >
                <DateRangePicker
                    minDate={minDate}
                    maxDate={maxDate}
                    options={options}
                    timeZone={timeZone}
                    value={selectedRange}
                    onChange={changeHandler}
                    onClose={() => setIsOpen(false)}
                />
            </DropdownContent>
        </Dropdown>
    );
};
